import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import { truncateString } from "utils/UtilityFuncs";
import ImportSWO from "./ImportSWO";

export default function SearchSWO({ btnClasses, refreshFunc }) {
  const [showModal, setShowModal] = useState(false);
  const dispatch = useDispatch();
  /*   All States
   ********************************************* */

  // States
  const [workOrders, setWorkOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [limit, setLimit] = useState(10);

  const [selected, setSelected] = useState(null);

  /*   All Functions
   ********************************************* */

  // Get work orders data
  const getWorkOrders = async () => {
    if (!showModal) return;

    setIsLoading(true);
    try {
      let body = {
        page: currentPage,
        pageLimit: limit,
        activeTab: "all",
      };

      if (searchQuery) {
        body.query = searchQuery;
      }

      const { data } = await API.post("/work-orders", body, {
        headers: authHeader(),
      });

      setWorkOrders(data.result);
      setTotalPages(data.pages);
      setIsLoading(false);
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
      setIsLoading(false);
    }
  };

  // Handle search submission
  const handleSearch = (e) => {
    e.preventDefault();
    getWorkOrders();
  };

  // Converting SQL timestamp to readable date
  const convertSqlTimestampToDate = (sqlTime) => {
    const timestamp = sqlTime;
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Pagination component
  const Pagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    pages.push(
      <li
        key="prev"
        className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
      >
        <button
          className="page-link"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
      </li>
    );

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePageChange(i)}>
            {i}
          </button>
        </li>
      );
    }

    // Next button
    pages.push(
      <li
        key="next"
        className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
      >
        <button
          className="page-link"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </li>
    );

    return (
      <nav aria-label="Work order pagination">
        <ul className="pagination justify-content-center mb-0">{pages}</ul>
      </nav>
    );
  };

  const handleSelect = (data) => {
    setSelected(data);
  };

  /*   All UseEffects
   ********************************************* */
  // Initial data load and when dependencies change
  useEffect(() => {
    if (showModal) {
      getWorkOrders();
    } else {
      setWorkOrders([]);
      setCurrentPage(1);
      setTotalPages(1);
      setSearchQuery("");
      setLimit(10);

      setSelected(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, currentPage, limit]);

  return (
    <>
      <button
        className={`${btnClasses ? btnClasses : ""} btn btn-primary`}
        onClick={() => setShowModal(true)}
      >
        Import S-WO
      </button>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Import Sales Work Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {workOrders.length > 0 ? (
            <>
              {!selected ? (
                <div className="import-swo-container">
                  <div className="mb-3 d-flex justify-content-between">
                    {/* Search Form */}
                    <form onSubmit={handleSearch} className="d-flex">
                      <input
                        type="text"
                        className="form-control me-2"
                        placeholder="Search work orders..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      <button type="submit" className="btn btn-primary">
                        Search
                      </button>
                    </form>

                    {/* Items per page selector */}
                    <div className="d-flex align-items-center">
                      <span className="me-2">Show:</span>
                      <select
                        className="form-select"
                        value={limit}
                        onChange={(e) => setLimit(Number(e.target.value))}
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={15}>15</option>
                        <option value={20}>20</option>
                      </select>
                    </div>
                  </div>

                  {/* Table */}
                  {isLoading ? (
                    <div className="text-center py-4">
                      <Spinner />
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <table className="table table-striped table-hover">
                          <thead>
                            <tr>
                              <th scope="col">W-ID</th>
                              <th scope="col">Customer Name</th>
                              <th scope="col">Job Name</th>
                              <th scope="col">Created Date</th>
                              <th scope="col">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {workOrders.length > 0 ? (
                              workOrders.map((item) => (
                                <tr key={item.id}>
                                  <td className="my-0 py-1 align-middle">
                                    W-{item.woId}
                                  </td>
                                  <td className="my-0 py-1 align-middle">
                                    {truncateString(item.customerName, 20)}
                                  </td>
                                  <td className="my-0 py-1 align-middle">
                                    {truncateString(item.jobName, 20)}
                                  </td>
                                  <td className="my-0 py-1 align-middle">
                                    {convertSqlTimestampToDate(item.createdAt)}
                                  </td>

                                  <td className="my-0 py-1 align-middle">
                                    <button
                                      onClick={() => {
                                        handleSelect(item);
                                      }}
                                      className="btn btn-sm btn-success"
                                    >
                                      Select
                                    </button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="8" className="text-center py-3">
                                  No work orders found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && <Pagination />}
                    </>
                  )}
                  <Modal.Footer>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setShowModal(false)}
                    >
                      Close
                    </button>
                  </Modal.Footer>
                </div>
              ) : (
                <>
                  {selected && (
                    <ImportSWO
                      selected={selected}
                      setSelected={setSelected}
                      showModal={setShowModal}
                      refreshFunc={refreshFunc}
                    />
                  )}
                </>
              )}
            </>
          ) : (
            <div className="text-center py-4">
              <Spinner />
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
